<template>
  <GoogleMapLoader
    :mapConfig="mapConfig"
    :apiKey="apiKey"
    :google="google">
    <template slot-scope="{ google, map }">
      <MapLegend v-if="showMapLegend" :isInMapSearchPage="topMap" :isOutMap="isOutMap"/>
      <GoogleMapMarkers
        :gardens="gardens"
        :google="google"
        :map="map"
        :addInfoWindows="true"
      />
      <GoogleMapMarkerClusterer
        :gardens="gardens"
        :google="google"
        :map="map"/>
      <SearchAreaPolygon
        :google="google"
        :map="map"
      />
      <MapAttribution />
    </template>
  </GoogleMapLoader>
</template>

<script>
import { mapGetters } from 'vuex'

import GoogleMapsApiLoader from 'google-maps-api-loader'
import eventBus from '@/services/eventBus'

import GoogleMapLoader from '@/components/map/GoogleMapLoader'
import GoogleMapMarkers from '@/components/map/GoogleMapMarkers'
import GoogleMapMarkerClusterer from '@/components/map/GoogleMapMarkerClusterer'
import MapLegend from '@/components/map/MapLegend'
import SearchAreaPolygon from '@/components/map/SearchAreaPolygon'
import MapAttribution from '@/components/map/MapAttribution'

import { mapSettings } from '@/components/map/constants/mapSettings'

export default {
  name: 'ListMapWrapper',

  components: {
    GoogleMapLoader,
    GoogleMapMarkers,
    GoogleMapMarkerClusterer,
    MapLegend,
    SearchAreaPolygon,
    MapAttribution,
  },

  props: {
    topMap: {
      type: Boolean,
      default: false,
    },
    isOutMap: {
      type: Boolean,
      default: false,
    },
    showMapLegend: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
    google: null,
    initialiseMap: false,
    mapCenter: {
      lat: 0,
      lng: 0,
    },
    updatingId: null,
  }),

  async mounted() {
    const googleMapApi = await GoogleMapsApiLoader({
      apiKey: this.apiKey,
    })

    this.google = googleMapApi
  },

  computed: {
    ...mapGetters('garden', [
      'gardens',
    ]),

    ...mapGetters('location', [
      'selectedLocationLatLng',
      'selectedLocationBounds',
    ]),

    ...mapGetters('map', [
      'mapBounds',
      'geoBoundingBox',
      'searchBounds',
      'isSearchAreaLocked',
    ]),

    ...mapGetters('filter', [
      'sectionFilterSelected',
    ]),

    mapConfig() {
      return {
        ...mapSettings,
        center: this.mapCenter,
      }
    },
    gardenSearch() {
      return this.sectionFilterSelected('garden')
    },
  },

  watch: {
    async geoBoundingBox(newGeoBoundingBox, oldGeoBoundingBox) {
      if (newGeoBoundingBox && newGeoBoundingBox !== oldGeoBoundingBox && this.google) {
        if (!this.initialiseMap) {
          await this.getMapCenter()

          this.initialiseMap = true
        }
      }
    },
    async google(value) {
      if (value && this.geoBoundingBox) {
        if (!this.initialiseMap) {
          await this.getMapCenter()

          this.initialiseMap = true
        }
      }
    },
    initialiseMap(value) {
      if (value && this.mapCenter) {
        eventBus.$emit('initialize-map')
      }
    },
    selectedLocationLatLng(newLocation, oldLocation) {
      const timeCheck = newLocation?.time !== this.updatingId
      const coordsChanged = !oldLocation || (newLocation?.lat !== oldLocation?.lat || newLocation?.lng !== oldLocation?.lng)
      if (newLocation && (timeCheck && coordsChanged)) {
        this.updatingId = newLocation?.time

        // Skip map reinit while a county search area overlay is active;
        // tearing down the map slot would destroy the polygon/circle.
        if (this.isSearchAreaLocked) return

        this.setMapCenter()
      }
    },
    selectedLocationBounds(newBounds, oldBounds) {
      const timeCheck = newBounds?.time !== this.updatingId
      const noBounds = !oldBounds
      const coordsChanged = oldBounds && ((newBounds?.northeast?.lat !== oldBounds?.northeast?.lat || newBounds?.northeast?.lng !== oldBounds?.northeast?.lng) || (newBounds?.southwest?.lat !== oldBounds?.southwest?.lat || newBounds?.southwest?.lng !== oldBounds?.southwest?.lng))
      if (newBounds && (timeCheck && (noBounds || coordsChanged))) {
        this.updatingId = newBounds?.time

        // Same gate as above: polygon and circle modes are both locked.
        if (this.isSearchAreaLocked) return

        eventBus.$emit('reinitialize-map')
      }
    },
  },

  methods: {
    getMapCenter() {
      return new Promise((resolve) => {
        const topRight = new this.google.maps.LatLng(this.geoBoundingBox.top_right.lat, this.geoBoundingBox.top_right.lng)
        const bottomLeft = new this.google.maps.LatLng(this.geoBoundingBox.bottom_left.lat, this.geoBoundingBox.bottom_left.lng)

        const centerBounds = new this.google.maps.LatLngBounds()
        centerBounds.extend(bottomLeft)
        centerBounds.extend(topRight)

        this.mapCenter = {
          lat: centerBounds.getCenter().lat(),
          lng: centerBounds.getCenter().lng(),
        }
        resolve()
      })
    },
    setMapCenter() {
      this.mapCenter = this.selectedLocationLatLng
      eventBus.$emit('reinitialize-map')
    },

  },
}
</script>
